import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api'
import type { CreateTagData, Tag } from '../../types/agentTypes'

const TAGS_QUERY_KEY = ['tags']

export const useTags = () => {
  const queryClient = useQueryClient()

  const {
    data: tags = [],
    isLoading: isLoadingTags,
    error: tagsError,
    refetch: fetchTags,
  } = useQuery<Tag[], Error, string[]>({
    queryKey: TAGS_QUERY_KEY,
    queryFn: apiClient.tags.fetchTagsList,
    select: (tags: Tag[]) => tags.map(tag => tag.name),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  })

  // Mutation for creating a tag
  const createTagMutation = useMutation({
    mutationFn: (data: CreateTagData) => apiClient.tags.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
    },
  })

  // Mutation for deleting a tag
  const deleteTagMutation = useMutation({
    mutationFn: (tagId: string) => apiClient.tags.deleteTag(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
    },
  })

  return {
    // Data
    tags,
    isLoadingTags,
    tagsError: tagsError?.message || null,

    // Actions
    fetchTags,
    createTag: createTagMutation.mutateAsync,
    deleteTag: deleteTagMutation.mutateAsync,

    // Mutation states
    isCreatingTag: createTagMutation.isPending,
    isDeletingTag: deleteTagMutation.isPending,

    // Mutation errors
    createTagError: createTagMutation.error?.message || null,
    deleteTagError: deleteTagMutation.error?.message || null,
  }
}
